import './styless.scss'
import {Close} from "@mui/icons-material";
import {Dialog, DialogContent, useMediaQuery} from "@mui/material";
import {useCallback, useEffect, useRef, useState} from "react";
import {getLocalStorage} from "../../util/storage";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../../store";
import {
    fetchNotifyUnreadCountAsync,
    setNotifyDialogTipsChange,
    setOpenMembershipPurchaseDialogChange,
    setOpenMemberBillDialogChange
} from "../../store/memberSlice";
import {getNotifyMessages, markNotifyMessagesRead, type NotifyMessageItem} from "../../api/common";
import {NotificationIconBlack, NotificationIconWhite} from "../../icons";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";

const formatTimeAgo = (isoTime?: string) => {
    if (!isoTime) return '';
    const created = new Date(isoTime).getTime();
    if (Number.isNaN(created)) return '';
    const diffMs = Date.now() - created;
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (diffMs < hour) return `${Math.max(1, Math.floor(diffMs / minute))}分钟前`;
    if (diffMs < day) return `${Math.max(1, Math.floor(diffMs / hour))}小时前`;
    return `${Math.max(1, Math.floor(diffMs / day))}天前`;
};

function NotifyIcon(props: { isRead?: boolean }) {
    const { isRead } = props;
    return (
        <div className='notify-icon'>
            <div className='notify-icon-main'><NotificationIconWhite /></div>
            {!isRead && <div className='notify-icon-dot' />}
        </div>
    );
}

const NOTIFY_PAGE_LIMIT = 20;

export default function NotificationService() {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<NotifyMessageItem | null>(null);
    const [items, setItems] = useState<NotifyMessageItem[]>([]);

    const dispatch: AppDispatch = useDispatch();
    const { trackNotificationClick, trackNotificationGoClick } = useMatomoTrackingPoint();

    const notifyDialogTips = useSelector((state: RootState) => state.member.notifyDialogTips);

    const memberInfo = useSelector((state: RootState) => state.member.memberInfo);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const contentWrapRef = useRef<HTMLDivElement | null>(null);

    const fetchNotifyMessages = useCallback(async (nextPage: number, reset = false) => {
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const response = await getNotifyMessages({
                is_read: false,
                page: nextPage,
                limit: NOTIFY_PAGE_LIMIT
            });
            const notifyItems = response.items ?? [];
            setItems(prev => {
                if (reset) return notifyItems;
                const merged = [...prev, ...notifyItems];
                const seen = new Set<string>();
                return merged.filter(item => {
                    const key = item.id || `${item.title}-${item.createdAt}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            });
            setPage(nextPage);
            const totalPages = response.pagination?.totalPages ?? 0;
            setHasMore(nextPage < totalPages);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        const userInfo = getLocalStorage('userInfo');
        if (!userInfo || !notifyDialogTips) return;
        fetchNotifyMessages(1, true).then();
    }, [notifyDialogTips, memberInfo, fetchNotifyMessages]);

    useEffect(() => {
        if (!notifyDialogTips || loading || loadingMore || !hasMore) return;
        const container = contentWrapRef.current;
        if (!container) return;
        const shouldAutoLoadNext = container.scrollHeight <= container.clientHeight + 1;
        if (shouldAutoLoadNext) {
            fetchNotifyMessages(page + 1).then();
        }
    }, [items, page, hasMore, loading, loadingMore, notifyDialogTips, fetchNotifyMessages]);

    if (!notifyDialogTips) {
        return null;
    }

    const handleDetailAction = () => {
        const relatedUrl = selectedItem?.related_url;
        if (!relatedUrl) {
            setDetailOpen(false);
            return;
        }

        if (relatedUrl.startsWith('http://') || relatedUrl.startsWith('https://')) {
            window.open(relatedUrl, '_blank');
        } else if (relatedUrl === '/membership/purchase') {
            dispatch(setOpenMembershipPurchaseDialogChange(true));
        } else if (relatedUrl === '/membership/invoice') {
            dispatch(setOpenMemberBillDialogChange(true));
        }

        setDetailOpen(false);
        dispatch(setNotifyDialogTipsChange(false));
        trackNotificationGoClick({ page_id: window.location.href, select_content: selectedItem?.title, switch_status: selectedItem?.related_url })
    };

    return (
        <>
            <div className='notification-service'>
                <div className='header'>
                    <div className='notification-title'>消息中心</div>
                    <div className='header-spacer' />
                    <Close className='close-icon' onClick={()=>{
                        dispatch(setNotifyDialogTipsChange(false))
                    }} />
                </div>

                <div
                    className='content-wrap'
                    ref={contentWrapRef}
                    onScroll={(event) => {
                        if (loading || loadingMore || !hasMore) return;
                        const target = event.currentTarget;
                        const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 40;
                        if (isNearBottom) {
                            fetchNotifyMessages(page + 1).then();
                        }
                    }}
                >
                    <div
                        className='list'
                    >
                        {items.length === 0 ? (
                            <div className='empty-state'>
                                <span className='empty-state-text'>暂无消息记录</span>
                            </div>
                        ) : (
                            items.map((item, idx) => (
                                <div
                                    key={item.id || `${item.title}-${idx}`}
                                    className={`item ${idx === items.length - 1 ? 'last' : ''}`}
                                    onClick={async () => {
                                        if (!item.is_read && item.id) {
                                            await markNotifyMessagesRead([item.id]);
                                            setItems(prev =>
                                                prev.map(msg =>
                                                    msg.id === item.id ? {...msg, is_read: true} : msg
                                                )
                                            );
                                            dispatch(fetchNotifyUnreadCountAsync());
                                        }
                                        setSelectedItem(item);
                                        setDetailOpen(true);
                                        trackNotificationClick({ page_id: window.location.href, select_content: item.title })
                                    }}
                                >
                                    <div className='icon-wrap'>
                                        <NotifyIcon isRead={item.is_read} />
                                    </div>
                                    <div className='item-content'>
                                        <div className='item-title'>{item.title}</div>
                                        <div className='item-desc'>{item.content}</div>
                                        <div className='item-time'>{formatTimeAgo(item.createdAt)}</div>
                                    </div>
                                </div>
                            ))
                        )}
                        {items.length > 0 && (
                            <div className='list-footer'>
                                {loadingMore ? '加载中...' : hasMore ? '上滑加载更多' : '没有更多了'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                fullScreen={isMobile}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: 'none',
                        borderRadius: isMobile ? 0 : '20px'
                    },
                    '& .MuiDialogContent-root': {
                        padding: 0
                    }
                }}
            >
                <DialogContent>
                    <div className='notification-detail'>
                        <div className='notification-detail-header'>
                            <div className='notification-detail-header-left'>
                                <span className='notification-detail-header-icon'><NotificationIconBlack /></span>
                                <span className='notification-detail-header-title'>系统消息</span>
                            </div>
                            <Close
                                className='notification-detail-header-close'
                                onClick={() => setDetailOpen(false)}
                            />
                        </div>

                        <div className='notification-detail-content'>
                            <div className='notification-detail-title'>{selectedItem?.title || '会员即将到期'}</div>
                            <div className='notification-detail-desc'>{selectedItem?.content || '您申请的发票已开具，请查收。'}</div>
                            <div className='notification-detail-time'>{formatTimeAgo(selectedItem?.createdAt) || '2天前'}</div>
                        </div>

                        <div className='notification-detail-footer'>
                            <div
                                className={`notification-detail-btn ${selectedItem?.related_url ? 'notification-detail-btn-action' : ''}`}
                                onClick={handleDetailAction}
                            >
                                {selectedItem?.related_url ? '立即前往' : '我知道了'}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}